import { Button, CustomSelect, FileUpload, SelectOption } from '@consigli/facade';
import {
  ExtendedFile,
  useChunkedBlobUpload,
  useCompleteQaSubmissionMutation,
  useProject,
  useProjectId,
  useRequestQaSubmissionUploadIntentMutation,
} from '@consigli/hooks';
import { TFunction } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useNavigationHistory } from '@/features/package-creation/use-navigation-history';
import { Nav } from '@/layouts/nav';

import { QaAgentUploadInfo } from './components/qa-agent-upload-info';

const ACCEPTED_EXTENSIONS = ['.rvt'];

const DESIGN_STAGE_VALUES = ['concept', 'scheme', 'detailed'] as const;
const AGENT_VALUES = [
  'mechanical',
  'structural',
  'electrical',
  'public_health',
  'fire',
  'architecture',
  'clash_detection',
] as const;

type DesignStage = (typeof DESIGN_STAGE_VALUES)[number];
type AgentName = (typeof AGENT_VALUES)[number];

interface QaAgentFormValues extends FieldValues {
  designStage: DesignStage;
  agents: AgentName[];
}

const isValidFileExtension = (filename: string): boolean =>
  ACCEPTED_EXTENSIONS.some((ext) => filename.toLowerCase().endsWith(ext));

const buildOptions = <T extends string>(
  values: readonly T[],
  translationKey: string,
  t: TFunction,
): SelectOption[] => values.map((value) => ({ value, label: t(`${translationKey}.${value}`) }));

const LABEL_CLASS = 'block text-base font-semibold text-day-neutral-subtle mb-2';
const RequiredMark = () => <span className="text-day-error-default">*</span>;

export const QaAgentWizard = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { project } = useProject(projectId);
  const { wizardBackButtonClick } = useNavigationHistory();

  const [files, setFiles] = useState<ExtendedFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const [requestUploadIntent] = useRequestQaSubmissionUploadIntentMutation();
  const [completeSubmission] = useCompleteQaSubmissionMutation();
  const upload = useChunkedBlobUpload();

  const methods = useForm<QaAgentFormValues>({
    defaultValues: {
      designStage: 'detailed',
      agents: [],
    },
  });

  const { handleSubmit, setValue, watch } = methods;

  const designStage = watch('designStage');
  const selectedAgents = watch('agents');

  const designStageOptions = useMemo(
    () => buildOptions(DESIGN_STAGE_VALUES, 'package-creation.qa-agent.design-stage-options', t),
    [t],
  );

  const agentOptions = useMemo(
    () => buildOptions(AGENT_VALUES, 'package-creation.qa-agent.agent-options', t),
    [t],
  );

  const handleFilesChange = useCallback(
    (value: React.SetStateAction<File[]>) => {
      const newFiles = typeof value === 'function' ? value(files) : value;

      // Handle file removal
      if (newFiles.length < files.length) {
        setFiles(newFiles as ExtendedFile[]);
        return;
      }

      // Filter for new files only
      const addedFiles = newFiles.filter(
        (newFile) => !files.some((existingFile) => existingFile.name === newFile.name),
      );

      const invalidFiles = addedFiles.filter((file) => !isValidFileExtension(file.name));
      const validFiles = addedFiles.filter((file) => isValidFileExtension(file.name));

      if (invalidFiles.length > 0) {
        if (invalidFiles.length === 1) {
          toast.error(`${invalidFiles[0].name} ${t('package-creation.qa-agent.file-invalid')}`);
        } else {
          toast.error(`${invalidFiles.length} ${t('package-creation.qa-agent.files-rejected')}`);
        }
      }

      setFiles((prev) => [...prev, ...validFiles]);
    },
    [files, t],
  );

  const onSubmit = useCallback(
    async (data: QaAgentFormValues) => {
      if (files.length === 0) {
        toast.error(t('package-creation.qa-agent.file-required'));
        return;
      }

      if (!project) {
        toast.error(t('package-creation.qa-agent.project-loading-error'));
        return;
      }

      const file = files[0];

      try {
        setIsProcessing(true);

        // 1. Ask core-api to allocate a submission and mint a SAS upload URL.
        const intent = await requestUploadIntent({
          projectName: project.name,
          country: project.country,
          designStage: data.designStage,
          agents: data.agents,
          filename: file.name,
          sizeBytes: file.size,
        }).unwrap();

        // 2. Upload the file in chunks directly to Azure Blob via the SAS URL.
        //    Bytes never touch core-api or qa-agent.
        await upload.upload(intent.uploadUrl, file);

        // 3. Confirm the upload; core-api enqueues a job for the qa-agent worker
        //    (which processes the model and emails the report).
        await completeSubmission({ submissionId: intent.submissionId }).unwrap();

        toast.success(t('package-creation.qa-agent.submit-success'));
      } catch (error) {
        console.error('Error submitting to Model Review Agent:', error);
        toast.error(t('package-creation.qa-agent.submit-error'));
      } finally {
        setIsProcessing(false);
      }
    },
    [files, t, project, requestUploadIntent, upload, completeSubmission],
  );

  return (
    <div className="w-full flex flex-col min-h-[calc(100vh-theme(space.14))] bg-day-light-4">
      <Nav projectId={projectId} handleClick={wizardBackButtonClick} />
      <div className="flex flex-col flex-1 items-center px-4 py-12 w-full sm:w-[50rem] mx-auto">
        <div className="mb-10 font-semibold text-2xl text-day-neutral-dark">
          {t('package-creation.new.qa-agent')}
        </div>

        <div className="w-full mb-8">
          <QaAgentUploadInfo />
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full">
            {/* Design Stage */}
            <div className="mb-6">
              <label className={LABEL_CLASS}>
                {t('package-creation.qa-agent.design-stage')} <RequiredMark />
              </label>
              <CustomSelect
                options={designStageOptions}
                value={designStageOptions.filter((option) => option.value === designStage)}
                onChange={(selectedOptions) => {
                  if (selectedOptions.length > 0) {
                    setValue('designStage', selectedOptions[0].value as DesignStage, {
                      shouldValidate: true,
                    });
                  }
                }}
                placeholder={t('package-creation.qa-agent.design-stage-placeholder')}
              />
            </div>

            {/* Agents (optional multi-select) */}
            <div className="mb-6">
              <label className={LABEL_CLASS}>{t('package-creation.qa-agent.agents')}</label>
              <p className="text-sm text-day-neutral-muted mb-2">
                {t('package-creation.qa-agent.agents-helper')}
              </p>
              <CustomSelect
                isMulti
                options={agentOptions}
                value={agentOptions.filter((option) =>
                  selectedAgents.includes(option.value as AgentName),
                )}
                onChange={(selectedOptions) => {
                  setValue(
                    'agents',
                    selectedOptions.map((option) => option.value as AgentName),
                  );
                }}
                placeholder={t('package-creation.qa-agent.agents-placeholder')}
              />
            </div>

            {/* File Upload Section */}
            <div className="mb-6">
              <p className={LABEL_CLASS}>
                {t('package-creation.qa-agent.file-label')} <RequiredMark />
              </p>
              <p className="text-sm text-day-neutral-muted mb-2">
                {t('package-creation.qa-agent.file-format')}
              </p>
              <FileUpload
                files={files}
                setFiles={handleFilesChange}
                dropAreaLabel={t('document-upload.drag-n-drop-here')}
                dropAreaClassName="mt-2 h-64 w-full"
                fileButtonLabel={t('document-upload.browse-files')}
                folderButtonLabel={t('document-upload.browse-folders')}
                uploadListLabel={t('package-creation.qa-agent.uploaded-files')}
                zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-center mt-8">
              <Button
                primary
                type="submit"
                rounded
                disabled={isProcessing || files.length === 0 || !project}
                className="px-8 py-3 font-semibold"
              >
                {!isProcessing && t('package-creation.qa-agent.submit')}
                {isProcessing &&
                  (upload.isUploading && upload.total > 0
                    ? `${t('package-creation.qa-agent.processing')} ${Math.round(upload.progress * 100)}%`
                    : t('package-creation.qa-agent.processing'))}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};
